if (!customElements.get('product-resource-selectors')) {
  class ProductResourceSelectors extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.selectors = Array.from(
        this.querySelectorAll('[data-product-resource-selector]')
      );

      this.selectors.forEach((selector) => this.bindSelector(selector));
      document.addEventListener('click', this.handleDocumentClick, {
        signal: this.signal
      });
      this.addEventListener('keydown', this.handleKeydown, {
        signal: this.signal
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      this.signal = null;
      this.selectors = [];
    }

    bindSelector(selector) {
      const trigger = selector.querySelector('[data-resource-selector-trigger]');
      const search = selector.querySelector('[data-resource-selector-search]');
      if (!trigger) return;

      trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (selector.classList.contains('is-open')) {
            this.closeSelector(selector);
          } else {
            this.openSelector(selector);
          }
        },
        { signal: this.signal }
      );

      search?.addEventListener(
        'input',
        () => this.filterSelector(selector, search.value),
        { signal: this.signal }
      );

      selector.addEventListener(
        'click',
        (event) => {
          const link = event.target.closest?.(
            '[data-resource-selector-item] a[href]'
          );
          if (!link) return;
          const item = link.closest('[data-resource-selector-item]');
          const isRetailerSelector = selector.classList.contains(
            'retailer-selector'
          );
          if (!isRetailerSelector) {
            const language = String(item?.dataset.value || '').trim();
            if (language) {
              window.MLLangFilter?.rememberManualLanguages?.([language]);
            }
            selector
              .querySelectorAll('[data-resource-selector-item]')
              .forEach((candidate) => {
                const selected = candidate === item;
                candidate.classList.toggle('is-selected', selected);
                if (candidate.hasAttribute('aria-selected')) {
                  candidate.setAttribute('aria-selected', selected.toString());
                }
              });
          }
          this.closeSelector(selector);
        },
        { signal: this.signal }
      );
    }

    openSelector(selector) {
      this.selectors.forEach((candidate) => {
        if (candidate !== selector) this.closeSelector(candidate);
      });

      const trigger = selector.querySelector('[data-resource-selector-trigger]');
      const panel = selector.querySelector('[data-resource-selector-panel]');
      const search = selector.querySelector('[data-resource-selector-search]');

      selector.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
      requestAnimationFrame(() => {
        if (this.isConnected && selector.classList.contains('is-open')) search?.focus();
      });
    }

    closeSelector(selector) {
      const trigger = selector.querySelector('[data-resource-selector-trigger]');
      const panel = selector.querySelector('[data-resource-selector-panel]');
      const search = selector.querySelector('[data-resource-selector-search]');

      selector.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      if (search) {
        search.value = '';
        this.filterSelector(selector, '');
      }
    }

    filterSelector(selector, query) {
      const normalize = window.MLLocalizationSearch?.normalize || ((value) =>
        String(value || '')
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .toLocaleLowerCase()
          .trim()
      );
      const normalizedQuery = normalize(query);
      selector
        .querySelectorAll('[data-resource-selector-item]')
        .forEach((item) => {
          let aliases = item.dataset.searchAliases || '';
          if (!aliases && item.dataset.searchLanguage && window.MLLocalizationSearch) {
            aliases = window.MLLocalizationSearch.languageAliases(
              item.dataset.searchLanguage
            );
            if (aliases) item.dataset.searchAliases = aliases;
          }
          const name = normalize(`${item.dataset.name || ''} ${aliases}`);
          item.hidden = !name.includes(normalizedQuery);
        });
    }

    handleDocumentClick = (event) => {
      this.selectors.forEach((selector) => {
        if (!selector.contains(event.target)) this.closeSelector(selector);
      });
    };

    handleKeydown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const selector = target.closest('[data-product-resource-selector]');
      if (!selector) return;

      if (event.key === 'Escape') {
        if (!selector.classList.contains('is-open')) return;
        event.preventDefault();
        this.closeSelector(selector);
        selector
          .querySelector('[data-resource-selector-trigger]')
          ?.focus();
        return;
      }

      const trigger = target.closest('[data-resource-selector-trigger]');
      if (trigger && event.key === 'ArrowDown') {
        event.preventDefault();
        this.openSelector(selector);
        return;
      }

      const search = target.closest('[data-resource-selector-search]');
      const currentLink = target.closest('[data-resource-selector-item] a[href]');
      if (!search && !currentLink) return;
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;

      const links = Array.from(
        selector.querySelectorAll('[data-resource-selector-item] a[href]')
      ).filter((link) => !link.closest('[data-resource-selector-item]')?.hidden);
      if (!links.length) return;

      event.preventDefault();
      if (search || event.key === 'Home') {
        links[0]?.focus();
        return;
      }
      if (event.key === 'End') {
        links.at(-1)?.focus();
        return;
      }

      const currentIndex = links.indexOf(currentLink);
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      links[(currentIndex + direction + links.length) % links.length]?.focus();
    };
  }

  customElements.define(
    'product-resource-selectors',
    ProductResourceSelectors
  );
}
